import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { MAX_FEEDBACK_LENGTH } from '../../common/feedback';
import { communicationStyles as styles } from './communicationStyles';

const FeedbackModal: React.FC<{ organizationId: string; onClose: () => void }> = ({ organizationId, onClose }) => {
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const dialog = useRef<HTMLDialogElement>(null);
    const submitting = useRef(false);

    useEffect(() => {
        const element = dialog.current;
        element?.showModal();
        return () => element?.close();
    }, []);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!text.trim() || submitting.current)
            return;
        submitting.current = true;
        setBusy(true);
        setError('');
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'manageFeedback');
            await callable({ action: 'submit', data: { organizationId, text: text.trim() } });
            setSubmitted(true);
        } catch (submitError) {
            setError(submitError instanceof Error ? submitError.message : 'Feedback could not be submitted. Please try again.');
        } finally {
            submitting.current = false;
            setBusy(false);
        }
    };

    return createPortal(
        <dialog ref={dialog} aria-labelledby="feedback-title" onCancel={event => { if (submitting.current) event.preventDefault(); else onClose(); }} className={`m-auto w-[calc(100%_-_2rem)] max-w-lg p-6 backdrop:bg-black/60 ${styles.panel}`}>
            <h2 id="feedback-title" className={`text-xl ${styles.heading}`}>{submitted ? 'Feedback Submitted' : 'Submit Feedback'}</h2>
            {submitted ? <div className="mt-4 space-y-5"><p role="status" className={styles.muted}>Your feedback has been sent to our team.</p><button type="button" onClick={onClose} className={styles.primaryButton}>Done</button></div> : <form onSubmit={submit} className="mt-4 space-y-4">
                <p className={styles.muted}>Share suggestions or tell us how we can improve.</p>
                <div><label htmlFor="feedback-text" className={`mb-2 block text-sm ${styles.heading}`}>Your feedback</label><textarea id="feedback-text" autoFocus required rows={6} maxLength={MAX_FEEDBACK_LENGTH} value={text} onChange={event => setText(event.target.value)} disabled={busy} className={styles.input} /></div>
                {error && <p role="alert" className={styles.error}>{error}</p>}
                <div className="flex justify-end gap-3"><button type="button" disabled={busy} onClick={onClose} className={styles.secondaryButton}>Cancel</button><button type="submit" disabled={busy || !text.trim()} className={styles.primaryButton}>{busy ? 'Submitting…' : 'Submit Feedback'}</button></div>
            </form>}
        </dialog>,
        document.body,
    );
};

export default FeedbackModal;
